import { ReactElement, useMemo } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';

import { mapDataToUndefinedIfSkip } from 'modules/api/utils';
import { useConnection } from 'modules/auth/hooks/useConnection';
import { BaseIntro, BaseIntroProps } from 'modules/common/components/BaseIntro';
import { ZERO } from 'modules/common/const';
import { useGlobalTranslation } from 'modules/i18n/hooks/useGlobalTranslation';
import {
  EMPTY_POOL_ADDRESSES,
  useGetPoolAddressesQuery,
} from 'modules/pool/actions/getPoolAddresses';
import { EMPTY_POOLS, useGetPoolsQuery } from 'modules/pool/actions/getPools';
import { useGetPoolAPYs } from 'modules/pool/hooks/useGetPoolAPYs';

import { translation } from './translation';

export function Intro(): ReactElement {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.down('md'));

  const { t, keys } = useGlobalTranslation(translation);
  const { cacheTime } = useConnection();

  const {
    data: poolsAddresses = EMPTY_POOL_ADDRESSES,
    isLoading: isPoolsAddressesLoading,
  } = useGetPoolAddressesQuery(undefined, {
    refetchOnMountOrArgChange: cacheTime,
  });
  const { data: pools = EMPTY_POOLS, isLoading: isPoolsLoading } =
    useGetPoolsQuery(
      {
        addresses: poolsAddresses,
      },
      {
        skip: poolsAddresses.length === 0,
        selectFromResult: mapDataToUndefinedIfSkip,
        refetchOnMountOrArgChange: cacheTime,
      },
    );

  const isLoading = isPoolsAddressesLoading || isPoolsLoading;

  const totalStaked = useMemo(
    () => pools.reduce((acc, { tvl }) => acc.plus(tvl), ZERO),
    [pools],
  );

  const { avgAPY } = useGetPoolAPYs();

  const totalStakedContent = (
    isMd ? totalStaked.integerValue() : totalStaked.decimalPlaces(9)
  ).toFormat();

  const stats: BaseIntroProps['stats'] = useMemo(() => {
    return [
      {
        value: isLoading ? undefined : totalStakedContent,
        label: t(keys.total, { token: t(keys.tokens.main) }),
      },
      {
        value: isLoading
          ? undefined
          : t(keys.upTo, { apy: avgAPY.decimalPlaces(2).toFormat() }),
        label: t(keys.apy),
      },
      {
        value: isLoading ? undefined : pools.length.toString(),
        label: t(keys.activeValidators),
      },
    ];
  }, [
    avgAPY,
    isLoading,
    keys.activeValidators,
    keys.apy,
    keys.tokens.main,
    keys.total,
    keys.upTo,
    pools.length,
    t,
    totalStakedContent,
  ]);

  return (
    <BaseIntro
      stats={stats}
      title={t(keys.title, { token: t(keys.tokens.main) }, true)}
    />
  );
}
